import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../Provider';
import { supabase } from '../data/supabaseClient';
import { FaArrowRight, FaArrowLeft, FaUser, FaCar, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaBox } from 'react-icons/fa';
import PostDetailsModal from '../PostDetailsModal';
import Spinner from '../common/Spinner';
import EmptyState from '../common/EmptyState';
import { useTranslation } from 'react-i18next';
import './MyActivePosts.css';

// Memoized skeleton component for better performance
const SkeletonCard = React.memo(() => (
  <div className="myp-skeleton-card"></div>
));

const MyActivePosts = () => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [myPosts, setMyPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Create a ref for the scroll container
  const scrollContainerRef = useRef(null);
  
  // Memoized fetch function
  const fetchMyActiveItems = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Fetch both posts and packages concurrently for better performance
      const [postsResponse, packagesResponse] = await Promise.all([
        supabase
          .from('posts')
          .select(`
            id,
            role,
            travel_date,
            travel_time,
            address_from_city,
            address_to_city,
            address_from_street,
            address_to_street,
            distance_km,
            travel_type,
            status
          `)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .eq('is_completed', false)
          .order('created_at', { ascending: false })
          .limit(10), // Limit to improve performance
        
        supabase
          .from('packages')
          .select(`
            id,
            travel_date,
            travel_time,
            address_from_city,
            address_to_city,
            address_from_street,
            address_to_street,
            distance_km,
            travel_type,
            status
          `)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(10) // Limit to improve performance
      ]);

      if (postsResponse.error) throw postsResponse.error;
      if (packagesResponse.error) throw packagesResponse.error;

      // Process and combine data
      const processedPosts = postsResponse.data.map(post => ({
        ...post,
        itemType: 'post',
        matchCount: 0
      }));
      
      const processedPackages = packagesResponse.data.map(pkg => ({
        ...pkg,
        role: 'Package',
        itemType: 'package',
        matchCount: 0
      }));
      
      const allItems = [...processedPosts, ...processedPackages].sort((a, b) => {
        return new Date(b.travel_date) - new Date(a.travel_date);
      });

      setMyPosts(allItems);
    } catch (error) {
      console.error("Error fetching my posts and packages:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchMyActiveItems();
  }, [fetchMyActiveItems]);

  // Memoized scroll handlers
  const handleScrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  }, []);

  const handleScrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  }, []);

  // Memoized modal handlers
  const handleItemClick = useCallback((item) => {
    setSelectedPost(item);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPost(null);
  }, []);

  // Memoized date formatter
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }, []);

  // Memoized time formatter - show only hours and minutes
  const formatTime = useCallback((timeString) => {
    if (!timeString) return '';
    // Remove seconds if present (HH:MM:SS -> HH:MM)
    return timeString.substring(0, 5);
  }, []);

  // Skeleton loader
  const renderSkeleton = () => (
    <div className="myp-skeleton">
      {[1, 2, 3].map((index) => (
        <SkeletonCard key={`skeleton-${index}`} />
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="myp-container">
        <div className="myp-header">
          <h3>{t('posts.my_posts')}</h3>
        </div>
        {renderSkeleton()}
      </div>
    );
  }

  if (error) {
    return (
      <div className="myp-container myp-error">
        <p>{t('errors.post_load_error')}: {error}</p>
      </div>
    );
  }

  if (myPosts.length === 0 && !isLoading) {
    return (
      <div className="myp-container myp-empty-container">
        <EmptyState
          variant="posts"
          title={t('empty_states.no_posts_title')}
          description={t('empty_states.no_posts_description')}
          actionLabel={t('empty_states.create_post')}
          onAction={() => navigate('/publish-post')}
        />
      </div>
    );
  }

  return (
    <div className="myp-container">
      <div className="myp-header">
        <h3>{t('posts.my_posts')}</h3>
        {myPosts.length > 0 && (
          <div className="myp-scroll-controls">
            <button 
              className="myp-scroll-button" 
              onClick={handleScrollLeft}
            >
              <FaArrowLeft />
            </button>
            <button 
              className="myp-scroll-button" 
              onClick={handleScrollRight}
            >
              <FaArrowRight />
            </button>
          </div>
        )}
      </div>

      <div className="myp-scroll-container" ref={scrollContainerRef}>
        {myPosts.map(post => (
          <div key={post.id} className='myp-post-card-frame'>
            <div 
              className="myp-post-card" 
              onClick={() => handleItemClick(post)}
            >
              <div className="myp-role-indicator">
                {post.role === "Driver" ? (
                  <FaCar className="myp-role-icon driver" />
                ) : post.role === "Passenger" ? (
                  <FaUser className="myp-role-icon passenger" />
                ) : (
                  <FaBox className="myp-role-icon package" />
                )}
              </div>
              
              <div className="myp-post-details">
                <div className="myp-post-locations">
                  <div className="myp-post-location from">
                    <FaMapMarkerAlt className="myp-location-icon" />
                    <span className="myp-location-text">{post.address_from_city || post.address_from_street}</span>
                  </div>
                  <div className="myp-post-connector"></div>
                  <div className="myp-post-location to">
                    <FaMapMarkerAlt className="myp-location-icon" />
                    <span className="myp-location-text">{post.address_to_city || post.address_to_street}</span>
                  </div>
                </div>
                
                <div className="myp-post-meta">
                  <div className="myp-post-time">
                    <FaCalendarAlt className="myp-meta-icon" />
                    <span>{formatDate(post.travel_date)}</span>
                  </div>
                  <div className="myp-post-time">
                    <FaClock className="myp-meta-icon" />
                    <span>{formatTime(post.travel_time)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && selectedPost && (
        <PostDetailsModal
          isOpen={isModalOpen}
          postId={selectedPost.id}
          postType={selectedPost.itemType || 'post'}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default MyActivePosts;
